/* This is the root error boundary for catastrophic failures that should never happen. */
/* eslint-disable i18next/no-literal-string -- If we reach here, the translation
   system itself may have failed, so we intentionally use hardcoded strings to be as
   simple and reliable as possible. */
import type {ReactNode} from 'react'
import {Component} from 'react'
import {ChromelessErrorModal} from '@freckle/educator-materials/ts/components/chromeless-error-modal'
import * as Bugsnag from '@freckle/classroom/ts/common/helpers/exception-handlers/bugsnag-client'
import {UserVisiblePageError} from '@freckle/classroom/ts/errors/user-visible-page-error'
import {isChromelessPath} from '@freckle/classroom/ts/hooks/chromeless/is-chromeless-path'
import {
  mkFailureToLoad,
  postMessageToWildcard
} from '@freckle/classroom/ts/hooks/use-chromeless-post-message'

type Props = {
  children: ReactNode
}

type State = {
  error: unknown
}

/**
 * Root-level error boundary for the React render tree
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {error: null}
  }

  static getDerivedStateFromError(error: unknown): State {
    return {error}
  }

  // eslint-disable-next-line class-methods-use-this -- React lifecycle method must be an instance method
  componentDidCatch(error: Error) {
    Bugsnag.logError(error)

    if (isChromelessPath()) {
      postMessageToWildcard({
        message:
          error instanceof UserVisiblePageError && error.chromelessPostMessage !== undefined
            ? error.chromelessPostMessage
            : mkFailureToLoad()
      })
    }
  }

  render(): ReactNode {
    const {error} = this.state
    const {children} = this.props

    if (error === null) {
      return children
    }

    if (isChromelessPath()) {
      const title = error instanceof UserVisiblePageError ? error.title : 'Something went wrong'
      const hideChrome = error instanceof UserVisiblePageError ? error.hideChrome : false
      return <ChromelessErrorModal title={title} hideChrome={hideChrome} />
    }

    return (
      <div style={{padding: '20px', textAlign: 'center'}}>
        <h1>Something went wrong</h1>
        <p>Please refresh the page and try again.</p>
      </div>
    )
  }
}
